import polygonClipping, {
  type MultiPolygon as ClippingMultiPolygon,
  type Polygon as ClippingPolygon,
} from 'polygon-clipping';
import type { Line, MultiPolygon, Point, Polygon, Ring } from './types';

export const GEOMETRY_EPSILON = 1e-7;

export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function closeRing(points: readonly Point[]): Ring {
  if (points.length === 0) return [];
  const ring = points.map((point) => ({ ...point }));
  if (distance(ring[0], ring[ring.length - 1]) > GEOMETRY_EPSILON) ring.push({ ...ring[0] });
  return ring;
}

export function ringSignedArea(points: readonly Point[]): number {
  if (points.length < 3) return 0;
  let twiceArea = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    twiceArea += current.x * next.y - next.x * current.y;
  }
  return twiceArea / 2;
}

export function ringArea(points: readonly Point[]): number {
  return Math.abs(ringSignedArea(points));
}

export function polygonArea(polygon: Polygon): number {
  return Math.max(
    0,
    ringArea(polygon.outer) - polygon.holes.reduce((total, hole) => total + ringArea(hole), 0),
  );
}

export function multiPolygonArea(polygons: readonly Polygon[]): number {
  return polygons.reduce((total, polygon) => total + polygonArea(polygon), 0);
}

function toClippingPolygon(polygon: Polygon): ClippingPolygon {
  return [polygon.outer, ...polygon.holes].map((ring) =>
    closeRing(ring).map((point) => [point.x, point.y]),
  );
}

function fromClippingMultiPolygon(polygons: ClippingMultiPolygon): MultiPolygon {
  return polygons.map((polygon) => ({
    outer: polygon[0].map(([x, y]) => ({ x, y })),
    holes: polygon.slice(1).map((ring) => ring.map(([x, y]) => ({ x, y }))),
  }));
}

export function intersectPolygons(subject: Polygon | MultiPolygon, clip: Polygon): MultiPolygon {
  const subjectGeometry = Array.isArray(subject)
    ? subject.map(toClippingPolygon)
    : toClippingPolygon(subject);
  return fromClippingMultiPolygon(
    polygonClipping.intersection(subjectGeometry, toClippingPolygon(clip)),
  );
}

export function unionArea(a: Polygon, b: Polygon): number {
  return multiPolygonArea(
    fromClippingMultiPolygon(polygonClipping.union(toClippingPolygon(a), toClippingPolygon(b))),
  );
}

export function intersectionArea(a: Polygon, b: Polygon): number {
  return multiPolygonArea(
    fromClippingMultiPolygon(
      polygonClipping.intersection(toClippingPolygon(a), toClippingPolygon(b)),
    ),
  );
}

function perpendicular(a: Point, b: Point, c: Point): number {
  return Math.abs((b.y - a.y) * c.x - (b.x - a.x) * c.y + b.x * a.y - b.y * a.x);
}

function simplifyOpenPath(points: readonly Point[], tolerance: number): Ring {
  if (points.length <= 2) return points.map((point) => ({ ...point }));
  const first = points[0];
  const last = points[points.length - 1];
  const denominator = Math.max(distance(first, last), GEOMETRY_EPSILON);
  let furthestIndex = -1;
  let furthestDistance = 0;
  for (let index = 1; index < points.length - 1; index += 1) {
    const nextDistance = perpendicular(first, last, points[index]) / denominator;
    if (nextDistance > furthestDistance) {
      furthestDistance = nextDistance;
      furthestIndex = index;
    }
  }
  if (furthestDistance <= tolerance || furthestIndex < 0) return [{ ...first }, { ...last }];
  const left = simplifyOpenPath(points.slice(0, furthestIndex + 1), tolerance);
  const right = simplifyOpenPath(points.slice(furthestIndex), tolerance);
  return [...left.slice(0, -1), ...right];
}

export function simplifyPath(points: readonly Point[], tolerance = 0.004): Ring {
  if (points.length > 3 && distance(points[0], points[points.length - 1]) <= tolerance) {
    let openLength = points.length;
    while (openLength > 3 && distance(points[0], points[openLength - 1]) <= tolerance) {
      openLength -= 1;
    }
    return simplifyOpenPath(points.slice(0, openLength), tolerance);
  }
  return simplifyOpenPath(points, tolerance);
}

function orientation(a: Point, b: Point, c: Point): number {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

function segmentsCross(a: Point, b: Point, c: Point, d: Point): boolean {
  const first = orientation(a, b, c);
  const second = orientation(a, b, d);
  const third = orientation(c, d, a);
  const fourth = orientation(c, d, b);
  return first * second < -GEOMETRY_EPSILON && third * fourth < -GEOMETRY_EPSILON;
}

export function ringSelfIntersects(points: readonly Point[]): boolean {
  const ring = closeRing(points);
  const edgeCount = ring.length - 1;
  for (let first = 0; first < edgeCount; first += 1) {
    for (let second = first + 1; second < edgeCount; second += 1) {
      if (Math.abs(first - second) <= 1 || (first === 0 && second === edgeCount - 1)) continue;
      if (segmentsCross(ring[first], ring[first + 1], ring[second], ring[second + 1])) return true;
    }
  }
  return false;
}

export function isValidRing(points: readonly Point[], minimumArea = 1e-4): boolean {
  const unique = new Set(points.map((point) => `${point.x.toFixed(7)},${point.y.toFixed(7)}`));
  return unique.size >= 3 && ringArea(points) >= minimumArea && !ringSelfIntersects(points);
}

export function normalizeRing(points: readonly Point[]): Ring {
  if (points.length === 0) return [];
  const minX = Math.min(...points.map((point) => point.x));
  const maxX = Math.max(...points.map((point) => point.x));
  const minY = Math.min(...points.map((point) => point.y));
  const maxY = Math.max(...points.map((point) => point.y));
  const width = maxX - minX;
  const height = maxY - minY;
  const scale = Math.max(width, height);
  if (scale <= GEOMETRY_EPSILON) return [];
  const xPadding = (1 - width / scale) / 2;
  const yPadding = (1 - height / scale) / 2;
  return points.map((point) => ({
    x: xPadding + (point.x - minX) / scale,
    y: yPadding + (point.y - minY) / scale,
  }));
}

export function fitRingToRing(
  target: readonly Point[],
  reference: readonly Point[],
  allowStretch = false,
): Ring {
  if (target.length === 0 || reference.length === 0) return [];
  const bounds = (ring: readonly Point[]) => {
    const minX = Math.min(...ring.map((point) => point.x));
    const maxX = Math.max(...ring.map((point) => point.x));
    const minY = Math.min(...ring.map((point) => point.y));
    const maxY = Math.max(...ring.map((point) => point.y));
    return {
      centerX: (minX + maxX) / 2,
      centerY: (minY + maxY) / 2,
      width: maxX - minX,
      height: maxY - minY,
    };
  };
  const targetBounds = bounds(target);
  const referenceBounds = bounds(reference);
  if (targetBounds.width <= GEOMETRY_EPSILON || targetBounds.height <= GEOMETRY_EPSILON) {
    return [];
  }
  let scaleX: number;
  let scaleY: number;
  if (allowStretch) {
    scaleX = referenceBounds.width / targetBounds.width;
    scaleY = referenceBounds.height / targetBounds.height;
  } else {
    const scale =
      Math.max(referenceBounds.width, referenceBounds.height) /
      Math.max(targetBounds.width, targetBounds.height);
    scaleX = scale;
    scaleY = scale;
  }
  return target.map((point) => ({
    x: referenceBounds.centerX + (point.x - targetBounds.centerX) * scaleX,
    y: referenceBounds.centerY + (point.y - targetBounds.centerY) * scaleY,
  }));
}

export function polygonIoU(target: readonly Point[], attempt: readonly Point[]): number {
  const normalizedTarget = normalizeRing(target);
  const normalizedAttempt = normalizeRing(attempt);
  if (!isValidRing(normalizedTarget) || !isValidRing(normalizedAttempt)) return 0;
  const targetPolygon: Polygon = { outer: normalizedTarget, holes: [] };
  const attemptPolygon: Polygon = { outer: normalizedAttempt, holes: [] };
  const union = unionArea(targetPolygon, attemptPolygon);
  if (union <= GEOMETRY_EPSILON) return 0;
  return intersectionArea(targetPolygon, attemptPolygon) / union;
}

export function pointInRing(point: Point, ring: readonly Point[]): boolean {
  let inside = false;
  for (let current = 0, previous = ring.length - 1; current < ring.length; previous = current++) {
    const a = ring[current];
    const b = ring[previous];
    const crosses =
      a.y > point.y !== b.y > point.y &&
      point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x;
    if (crosses) inside = !inside;
  }
  return inside;
}

export function pointInPolygon(point: Point, polygon: Polygon): boolean {
  return (
    pointInRing(point, polygon.outer) && !polygon.holes.some((hole) => pointInRing(point, hole))
  );
}

function halfPlanePolygon(line: Line, side: 1 | -1): Polygon | null {
  const dx = line.end.x - line.start.x;
  const dy = line.end.y - line.start.y;
  const length = Math.hypot(dx, dy);
  if (length <= GEOMETRY_EPSILON) return null;
  const tangent = { x: dx / length, y: dy / length };
  const normal = { x: (-dy / length) * side, y: (dx / length) * side };
  const span = 20;
  const depth = 40;
  const first = {
    x: line.start.x - tangent.x * span,
    y: line.start.y - tangent.y * span,
  };
  const second = {
    x: line.start.x + tangent.x * span,
    y: line.start.y + tangent.y * span,
  };
  return {
    outer: [
      first,
      second,
      { x: second.x + normal.x * depth, y: second.y + normal.y * depth },
      { x: first.x + normal.x * depth, y: first.y + normal.y * depth },
    ],
    holes: [],
  };
}

export type CutResult = {
  first: MultiPolygon;
  second: MultiPolygon;
  firstArea: number;
  secondArea: number;
  totalArea: number;
  smallerPercent: number;
};

export function cutPolygon(polygon: Polygon, line: Line): CutResult | null {
  const positive = halfPlanePolygon(line, 1);
  const negative = halfPlanePolygon(line, -1);
  if (!positive || !negative) return null;
  let first: MultiPolygon;
  let second: MultiPolygon;
  try {
    first = intersectPolygons(polygon, positive);
    second = intersectPolygons(polygon, negative);
  } catch {
    // Robust clipping can still reject a line that lands exactly on a polygon vertex.
    // The gesture/generator treats that numerical degeneracy as a non-cut and safely retries.
    return null;
  }
  const firstArea = multiPolygonArea(first);
  const secondArea = multiPolygonArea(second);
  const totalArea = polygonArea(polygon);
  if (
    totalArea <= GEOMETRY_EPSILON ||
    firstArea <= GEOMETRY_EPSILON ||
    secondArea <= GEOMETRY_EPSILON ||
    Math.abs(firstArea + secondArea - totalArea) > Math.max(1e-6, totalArea * 1e-5)
  ) {
    return null;
  }
  return {
    first,
    second,
    firstArea,
    secondArea,
    totalArea,
    smallerPercent: (100 * Math.min(firstArea, secondArea)) / totalArea,
  };
}

export function lineThroughUnitBox(line: Line): Line | null {
  const dx = line.end.x - line.start.x;
  const dy = line.end.y - line.start.y;
  if (Math.hypot(dx, dy) <= GEOMETRY_EPSILON) return null;
  const hits: Point[] = [];
  const add = (x: number, y: number) => {
    if (
      x >= -GEOMETRY_EPSILON &&
      x <= 1 + GEOMETRY_EPSILON &&
      y >= -GEOMETRY_EPSILON &&
      y <= 1 + GEOMETRY_EPSILON
    ) {
      if (!hits.some((point) => distance(point, { x, y }) < 1e-6)) hits.push({ x, y });
    }
  };
  if (Math.abs(dx) > GEOMETRY_EPSILON) {
    add(0, line.start.y + ((0 - line.start.x) / dx) * dy);
    add(1, line.start.y + ((1 - line.start.x) / dx) * dy);
  }
  if (Math.abs(dy) > GEOMETRY_EPSILON) {
    add(line.start.x + ((0 - line.start.y) / dy) * dx, 0);
    add(line.start.x + ((1 - line.start.y) / dy) * dx, 1);
  }
  return hits.length >= 2 ? { start: hits[0], end: hits[1] } : null;
}

export function polygonToSvgPath(polygons: Polygon | MultiPolygon): string {
  const collection = Array.isArray(polygons) ? polygons : [polygons];
  return collection
    .flatMap((polygon) => [polygon.outer, ...polygon.holes])
    .map((ring) => {
      if (ring.length === 0) return '';
      return `M ${ring.map((point) => `${point.x} ${point.y}`).join(' L ')} Z`;
    })
    .join(' ');
}
